import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, useSortBy, useFilters } from "react-table";
import { useDropdown } from "../utils/utils";

const toggleColumnExport = (columns, setColumns) => (columnId, columnState) => {
  columns.forEach((colGr) => {
    const colF = colGr.columns.find((col) => col.accessor === columnId);
    if (colF) {
      colF.show = columnState;
    }
  });

  setColumns([...columns]);
};

// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      className="input"
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

// This is a custom filter UI for selecting
// a unique option from a list
function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      if (row.values[id] !== "" && row.values[id] !== undefined) {
        options.add(row.values[id]);
      }
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <div className="select">
      <select
        value={filterValue}
        onChange={(e) => {
          setFilter(e.target.value || undefined);
        }}
      >
        <option value="">All</option>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

// This is a custom filter UI that uses a
// slider to set the filter value between a column's
// min and max values
function SliderColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  // Calculate the min and max
  // using the preFilteredRows

  const [min, max] = React.useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach((row) => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <>
      <input
        className="slider" // component from bulma-slider
        type="range"
        min={min}
        max={max}
        value={filterValue || min}
        onChange={(e) => {
          setFilter(parseInt(e.target.value, 10));
        }}
      />
      <button className="button m-l-sm" onClick={() => setFilter(undefined)}>
        Off
      </button>
    </>
  );
}

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
function NumberRangeColumnFilter({
  column: { filterValue = [], preFilteredRows, setFilter, id },
}) {
  const [min, max] = React.useMemo(() => {
    const intRows = preFilteredRows
      .map((row) => parseInt(row.values[id]))
      .filter((elem) => !Number.isNaN(elem));
    let min = intRows.length ? intRows[0] : 0;
    let max = intRows.length ? intRows[0] : 0;

    intRows.forEach((row) => {
      min = Math.min(row, min);
      max = Math.max(row, max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
      }}
    >
      <input
        className="input"
        value={filterValue[0] || ""}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [
            val ? parseInt(val, 10) : undefined,
            old[1],
          ]);
        }}
        placeholder={`Min (${min})`}
        style={{
          width: "100px",
          marginRight: "0.5rem",
        }}
      />
      to
      <input
        className="input"
        value={filterValue[1] || ""}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [
            old[0],
            val ? parseInt(val, 10) : undefined,
          ]);
        }}
        placeholder={`Max (${max})`}
        style={{
          width: "100px",
          marginLeft: "0.5rem",
        }}
      />
    </div>
  );
}

const Table = ({
  columns,
  data,
  usersAwaiting,
  usersRejected,
  hiddenColumnsInit,
}) => {
  const filterTypes = React.useMemo(
    () => ({
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    [],
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    [],
  );

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setAllFilters,
    setFilter,
    toggleSortBy,
    setHiddenColumns: updateHiddenColumns,
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: {
        sortBy: [{ id: "signUpDate", desc: true }],
      },
    },
    useFilters,
    useSortBy,
  );

  const [hiddenColumns, setHiddenColumns] = useState(hiddenColumnsInit);

  useEffect(() => {
    updateHiddenColumns(hiddenColumns);
  }, [hiddenColumns, updateHiddenColumns]);

  const toggleColumn = (colId) => {
    if (hiddenColumns.includes(colId)) {
      setHiddenColumns(hiddenColumns.filter((col) => col !== colId));
    } else {
      setHiddenColumns([...hiddenColumns, colId]);
    }
  };

  return (
    <Fragment>
      <div className="is-size-6 m-b-lg">
        <p>Quick filters:</p>
        <p>
          <a
            className="dotted"
            onClick={() => {
              setAllFilters([]);
              setFilter("status", "awaiting");
              toggleSortBy("signUpDate", true);
            }}
          >
            Show unverified users
          </a>
          <span className="tag is-light m-l-sm">{usersAwaiting}</span>
        </p>
        <p>
          <a
            className="dotted"
            onClick={() => {
              setAllFilters([]);
              setFilter("status", "rejected");
              toggleSortBy("signUpDate", true);
            }}
          >
            Show rejected users
          </a>
          <span className="tag is-light m-l-sm">{usersRejected}</span>
        </p>
        {/* <p>
          <a className="dotted">Show archived users</a>
          <span className="tag is-light m-l-sm">0</span>
        </p> */}
      </div>
      <div
        className="panel-heading has-background-grey-lighter"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex" }}>
          <span className="">Users</span>
          <span className="tag is-light m-l-sm">{rows.length}</span>
        </span>
        <span>
          <div className="dropdown is-hoverable is-right">
            <div className="dropdown-trigger">
              <button
                className="button"
                aria-haspopup="true"
                aria-controls="dropdown-menu"
                type="button"
              >
                <span>Show/hide columns</span>
                <span className="icon is-small">
                  <i
                    className="fas fa-angle-down has-text-grey-light"
                    aria-hidden="true"
                  />
                </span>
              </button>
            </div>
            <div className="dropdown-menu" id="dropdown-menu" role="menu">
              <div className="dropdown-content columns is-desktop m-t-none m-r-none">
                {columns.map((colGr) => (
                  <div key={colGr.Header}>
                    <div className="dropdown-item has-text-grey has-text-centered">
                      {colGr.Header}
                    </div>
                    {colGr.columns.map((col) => (
                      <label
                        className="dropdown-item checkbox"
                        key={col.accessor}
                      >
                        <input
                          type="checkbox"
                          defaultChecked={!hiddenColumns.includes(col.accessor)}
                          className="m-r-sm"
                          onClick={() => {
                            toggleColumn(col.accessor);
                          }}
                        />
                        {col.Header}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="button m-l-md"
            onClick={() => {
              setAllFilters([]);
            }}
          >
            Reset filters
          </button>
        </span>
      </div>
      <div className="table-container p-b-xl">
        <table
          {...getTableProps({
            className: "table is-hoverable is-fullwidth is-bordered",
          })}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps({
                  className: "has-background-white-ter",
                })}
              >
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps({
                      className: column.depth === 0 && "has-text-centered",
                    })}
                  >
                    <span
                      {...column.getSortByToggleProps({
                        title:
                          "Click to sort, hold Shift and click to multi-sort",
                        style: {
                          whiteSpace: "nowrap",
                        },
                      })}
                    >
                      {column.render("Header")}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <i className="fas fa-arrow-down m-l-sm" />
                        ) : (
                          // <i className="fas fa-sort-amount-down m-l-sm" />
                          <i className="fas fa-arrow-up m-l-sm" />
                          // <i className="fas fa-sort-amount-up-alt m-l-sm" />
                        )
                      ) : (
                        ""
                      )}
                      {/* {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""} */}
                      {/* {column.isSorted ? (column.isSortedDesc ? " ↓" : " ↑") : ""} */}
                    </span>
                    <div className={column.canFilter ? "m-t-xs m-b-xs" : ""}>
                      {column.canFilter ? column.render("Filter") : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.map(
              (row, i) =>
                prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                ),
            )}
          </tbody>
        </table>
      </div>
    </Fragment>
  );
};

Table.propTypes = {};

export {
  Table as UsersTable,
  DefaultColumnFilter,
  SelectColumnFilter,
  SliderColumnFilter,
  NumberRangeColumnFilter,
  toggleColumnExport,
};
